import { useEffect, useRef, useState, type PointerEvent as PE } from 'react'
import { useNavigate } from 'react-router'
import { useChamada } from './chamada.tsx'
import { IconePlanta } from './IconePlanta.tsx'
import { Video } from './ReuniaoDaSala.tsx'
import './chamada.css'

/**
 * A chamada fora do Escritório (SPEC-20261010-1557): uma janelinha no canto de
 * cima à direita, que se arrasta para onde a pessoa quiser, como no Discord.
 * Mostra a tela que alguém apresenta (ou quem está de câmera); sem vídeo, só
 * as fotos de quem está na sala. Clicar nela volta para a reunião na hora.
 */

const CHAVE = 'orbind.chamada.janelinha'
const LARGURA = 280
type Pos = { x: number; y: number }

function posGuardada(): Pos | null {
  try {
    const p = JSON.parse(localStorage.getItem(CHAVE) ?? 'null') as Pos | null
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null
  } catch {
    return null
  }
}
const prender = (p: Pos, el: HTMLElement | null): Pos => {
  const w = el?.offsetWidth ?? LARGURA
  const h = el?.offsetHeight ?? 160
  return { x: Math.max(8, Math.min(window.innerWidth - w - 8, p.x)), y: Math.max(8, Math.min(window.innerHeight - h - 8, p.y)) }
}

export function JanelinhaDaChamada() {
  const ch = useChamada()
  const navegar = useNavigate()
  const caixa = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Pos>(() => posGuardada() ?? { x: window.innerWidth - LARGURA - 24, y: 72 })
  const arrasto = useRef<{ x: number; y: number; px: number; py: number; moveu: boolean } | null>(null)

  // a janela do navegador encolheu: a janelinha continua à vista
  useEffect(() => {
    const ajustar = () => setPos((p) => prender(p, caixa.current))
    ajustar()
    window.addEventListener('resize', ajustar)
    return () => window.removeEventListener('resize', ajustar)
  }, [])

  if (!ch?.sala) return null
  const { quemApresenta, quadros, sala } = ch
  const deCamera = quadros.find((q) => q.video && !q.eu) ?? quadros.find((q) => q.video) ?? null
  const abrir = () => navegar('/office/reuniao')

  const apertar = (e: PE<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return
    e.currentTarget.setPointerCapture(e.pointerId)
    arrasto.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y, moveu: false }
  }
  const mover = (e: PE<HTMLDivElement>) => {
    const a = arrasto.current
    if (!a) return
    const dx = e.clientX - a.x
    const dy = e.clientY - a.y
    if (!a.moveu && Math.hypot(dx, dy) > 4) a.moveu = true
    if (a.moveu) setPos(prender({ x: a.px + dx, y: a.py + dy }, caixa.current))
  }
  const soltar = () => {
    const a = arrasto.current
    arrasto.current = null
    if (!a) return
    if (!a.moveu) return abrir()
    setPos((p) => {
      try {
        localStorage.setItem(CHAVE, JSON.stringify(p))
      } catch {
        // sem armazenamento: a posição vale só nesta visita
      }
      return p
    })
  }

  const comVideo = quemApresenta?.tela ?? deCamera?.camera ?? null
  return (
    <div
      ref={caixa}
      className={`chm-janelinha${comVideo ? ' chm-janelinha--video' : ''}`}
      style={{ left: pos.x, top: pos.y }}
      role="dialog"
      aria-label={`Chamada em ${sala.nome}`}
      title="Clique para abrir a reunião; arraste para mudar de lugar"
      onPointerDown={apertar}
      onPointerMove={mover}
      onPointerUp={soltar}
    >
      {comVideo ? (
        <div className="chm-janelinha-tela">
          <Video stream={comVideo} espelhar={!quemApresenta && deCamera?.eu} className={`rs-video${quemApresenta ? ' rs-video--tela' : ''}`} />
          <span className="chm-janelinha-quem">
            {quemApresenta ? (
              <>
                <i className="fas fa-desktop" aria-hidden /> {quemApresenta.nome === 'Você' ? 'Você está apresentando' : `${quemApresenta.nome} apresentando`}
              </>
            ) : (
              (deCamera?.eu ? 'Você' : deCamera?.nome)
            )}
          </span>
        </div>
      ) : null}
      <div className="chm-janelinha-barra">
        <span className="chm-janelinha-sala">
          <i className="chm-ponto" aria-hidden />
          <span>
            <b>{sala.nome}</b>
            <small>{quadros.length === 1 ? 'Só você' : `${quadros.length} na chamada`}</small>
          </span>
        </span>
        <button type="button" className={`chm-mini${ch.micLigado ? '' : ' chm-mini--off'}`} aria-label={ch.micLigado ? 'Desligar microfone' : 'Ligar microfone'} title={ch.micLigado ? 'Desligar microfone' : 'Ligar microfone'} onClick={ch.alternarMic}>
          <i className={`fas ${ch.micLigado ? 'fa-microphone' : 'fa-microphone-slash'}`} aria-hidden />
        </button>
        <button type="button" className="chm-mini" aria-label="Abrir a reunião" title="Abrir a reunião" onClick={abrir}>
          <i className="fas fa-expand-alt" aria-hidden />
        </button>
        <button type="button" className="chm-mini" aria-label="Voltar ao tabuleiro" title="Voltar ao tabuleiro" onClick={() => navegar('/office')}>
          <IconePlanta tamanho={16} />
        </button>
        <button type="button" className="chm-mini chm-mini--sair" aria-label="Sair da chamada" title="Sair da chamada" onClick={ch.sair}>
          <i className="fas fa-phone-slash" aria-hidden />
        </button>
      </div>
    </div>
  )
}
